export interface AnomalyConfig {
  windowSize: number;
  sigmaThreshold: number;
}

export interface AnomalyResult {
  value: number;
  mean: number;
  stdDev: number;
  zScore: number;
  isAnomaly: boolean;
  severity: 'none' | 'low' | 'medium' | 'high';
}

const DEFAULT_CONFIG: AnomalyConfig = {
  windowSize: 20,
  sigmaThreshold: 2.5,
};

export class AnomalyDetector {
  private history: Map<string, number[]> = new Map();
  private config: AnomalyConfig;

  constructor(config?: Partial<AnomalyConfig>) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  detect(metric: string, value: number): AnomalyResult {
    if (!this.history.has(metric)) {
      this.history.set(metric, []);
    }

    const window = this.history.get(metric)!;
    window.push(value);

    if (window.length > this.config.windowSize) {
      window.shift();
    }

    if (window.length < 5) {
      return {
        value,
        mean: 0,
        stdDev: 0,
        zScore: 0,
        isAnomaly: false,
        severity: 'none',
      };
    }

    const mean = this.calculateMean(window);
    const stdDev = this.calculateStdDev(window, mean);
    const zScore = stdDev > 0 ? Math.abs((value - mean) / stdDev) : 0;
    const isAnomaly = zScore > this.config.sigmaThreshold;

    let severity: AnomalyResult['severity'] = 'none';
    if (isAnomaly) {
      if (zScore > this.config.sigmaThreshold * 2) severity = 'high';
      else if (zScore > this.config.sigmaThreshold * 1.5) severity = 'medium';
      else severity = 'low';
    }

    return { value, mean, stdDev, zScore, isAnomaly, severity };
  }

  reset(metric?: string): void {
    if (metric) {
      this.history.delete(metric);
    } else {
      this.history.clear();
    }
  }

  getHistorySize(metric: string): number {
    return this.history.get(metric)?.length ?? 0;
  }

  private calculateMean(values: number[]): number {
    return values.reduce((a, b) => a + b, 0) / values.length;
  }

  private calculateStdDev(values: number[], mean: number): number {
    const variance =
      values.reduce((acc, val) => acc + (val - mean) ** 2, 0) / values.length;
    return Math.sqrt(variance);
  }
}
